/**
 * Contact details form component for collecting customer and patient information in Provet online booking.
 *
 * This component manages a complex multi-section form with:
 * - Customer information (name, email, phone, address)
 * - Patient information (pet name, species, breed, notes)
 * - localStorage persistence ("Remember me" functionality)
 * - Real-time validation with error messages
 * - Automatic focus management for invalid fields
 * - Species/breed data restoration from saved state
 *
 * **Form Sections:**
 * 1. Customer Information - Required fields for pet owner details
 * 2. Patient Information - Required pet details, including notes
 * 3. Remember Details - Optional localStorage persistence toggle
 *
 * **Validation:**
 * - Client-side validation for all fields
 * - Integrates with Provet API field length requirements
 * - Shows errors after field blur or form submission attempt
 * - Auto-focuses first invalid field on validation failure
 *
 * **localStorage Features:**
 * - Saves form data when "Remember me" is checked
 * - Restores data on component mount if available
 * - Handles species/breed restoration after API data loads
 * - Removes data when checkbox is unchecked
 *
 * @param defaultSpecies - Optional pre-populated species list from server
 * @param patientInfo - Optional patient info for pre-filling form fields
 * @param onContactDetailsChanged - Callback invoked when form is valid and submitted.
 *                                   Receives complete contact details for both client and patient.
 * @param onValidationError - Optional callback invoked when validation fails.
 *                           Used for scrolling to first error or showing notifications.
 *
 * @example
 * ```tsx
 * <ProvetContactDetails
 *   defaultSpecies={serverSpeciesList}
 *   onContactDetailsChanged={(details) => {
 *     console.log('Customer:', details.client);
 *     console.log('Patient:', details.patient);
 *     proceedToNextStep(details);
 *   }}
 *   onValidationError={() => {
 *     scrollToFirstError();
 *     showNotification('Please fix validation errors');
 *   }}
 * />
 * ```
 */

import React, { useCallback, useEffect, useState } from "react";
import { useProvetContext, useTranslationContext } from "../../context";
import { useFormValidation } from "../../hooks";
import { focusContactDetailsForm } from "../../hooks/useProvetFocusManagement";
import {
  Breed,
  ContactDetailsEventArgs,
  FormSelectItem,
  OnlineBookingPatient,
  ProvetDialogStep,
  Species
} from "../../types";
import CustomerInfoSection from "./customerInfoSection";
import { useContactDetailsLocalStorage } from "./hooks/useContactDetailsLocalStorage";
import { useContactDetailsPersistence } from "./hooks/useContactDetailsPersistence";
import { useContactDetailsRestoration } from "./hooks/useContactDetailsRestoration";
import { useContactDetailsValidation } from "./hooks/useContactDetailsValidation";
import { useSpeciesAndBreeds } from "./hooks/useSpeciesAndBreeds";
import { useSpeciesBreedRestoration } from "./hooks/useSpeciesBreedRestoration";
import PatientInfoSection from "./patientInfoSection";
import { ProvetContactDetailsSectionHeader } from "./provetContactDetailsSectionHeader";
import { ProvetContactDetailsSubmit } from "./provetContactDetailsSubmit";
import RememberDetails from "./rememberDetails";

export interface ProvetContactDetailsProps {
  defaultSpecies?: FormSelectItem[];
  patientInfo?: OnlineBookingPatient;
  onContactDetailsChanged?: (contactDetails: ContactDetailsEventArgs) => void;
  onValidationError?: () => void;
  onSpeciesSelected?: (species: Species | undefined) => void;
}

export const ProvetContactDetails = ({
  defaultSpecies,
  patientInfo,
  onContactDetailsChanged,
  onValidationError,
  onSpeciesSelected
}: ProvetContactDetailsProps) => {
  const [rememberDetailsChecked, setRememberDetailsChecked] = useState(false);
  const [savedSpecies, setSavedSpecies] = useState<Species | undefined>(undefined);
  const [savedBreed, setSavedBreed] = useState<Breed | undefined>(undefined);
  const [isRestoring, setIsRestoring] = useState(true);

  const provetContext = useProvetContext();
  const translationContext = useTranslationContext();
  const { handleValidationError } = useFormValidation({
    scrollOffset: 180,
    scrollBehavior: "smooth",
    scrollDelay: 200
  });

  // Focus the first field when the form is shown and its step is open: right after the
  // phone verification, or when the step opens with the phone already verified. This is the
  // only place that focuses the form, and it never moves focus while the customer is in
  // another field (observation 24).
  const isStepActive =
    provetContext.bookingFlowState.activeStep === ProvetDialogStep.ContactDetails;

  useEffect(() => {
    if (!isStepActive) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      focusContactDetailsForm();
    });

    return () => cancelAnimationFrame(frame);
  }, [isStepActive]);

  // Use custom hooks
  const validation = useContactDetailsValidation();
  const speciesAndBreeds = useSpeciesAndBreeds({
    provetContext,
    defaultSpecies,
    patientInfo,
    onSpeciesAutoSelected: onSpeciesSelected // Pass the callback through
  });
  const { saveContactDetails, loadContactDetails, removeContactDetails } =
    useContactDetailsLocalStorage();

  // Restore contact details on mount, from the current booking or from localStorage
  useContactDetailsRestoration({
    loadContactDetails,
    existingContactDetails: provetContext.bookingFlowState.formData.contactDetails,
    validation,
    onRememberDetailsCheckedChange: setRememberDetailsChecked,
    onSpeciesSaved: setSavedSpecies,
    onBreedSaved: setSavedBreed,
    onRestorationComplete: () => {
      // Called when there's no saved data at all
      setIsRestoring(false);
    }
  });

  // Restore species/breed after lists are loaded
  useSpeciesBreedRestoration({
    savedSpecies,
    savedBreed,
    speciesAndBreeds,
    validation,
    onRestorationComplete: () => setIsRestoring(false),
    onSpeciesRestored: species => {
      // Notify parent component when species is restored from localStorage
      if (onSpeciesSelected) {
        onSpeciesSelected(species);
      }
    }
  });

  // Auto-save contact details to localStorage
  useContactDetailsPersistence({
    rememberDetailsChecked,
    isRestoring,
    validation,
    speciesAndBreeds,
    existingContactDetails: provetContext.bookingFlowState.formData.contactDetails,
    saveContactDetails,
    removeContactDetails
  });

  /**
   * Handles species selection and updates validation state.
   */
  const handleSpeciesSelected = (item: FormSelectItem): void => {
    speciesAndBreeds.onSpeciesSelected(item);
    const speciesId = parseInt(item.value);
    const foundSpecies = speciesAndBreeds.species.find(s => s.id === speciesId);
    validation.setPatientValidation(prev => ({
      ...prev,
      petSpeciesValid: !!foundSpecies
    }));

    // Notify parent component about species selection for compatibility checking
    if (onSpeciesSelected) {
      onSpeciesSelected(foundSpecies);
    }
  };

  /**
   * Callback for when contact details change.
   */
  const contactDetailsChanged = useCallback((): void => {
    // Preserve email and phoneNumber from phone verification step
    const existingContactDetails = provetContext.bookingFlowState.formData.contactDetails;

    const contactDetails: ContactDetailsEventArgs = {
      client: {
        firstName: validation.client.firstName,
        lastName: validation.client.lastName,
        email: existingContactDetails?.client?.email,
        phoneNumber: existingContactDetails?.client?.phoneNumber,
        streetAddress: validation.client.streetAddress || "",
        zipCode: validation.client.zipCode || "",
        city: validation.client.city || "",
        identificationNumber: validation.client.identificationNumber,
        extraField: validation.client.extraField // Honeypot field for bot detection
      },
      patient: {
        species: speciesAndBreeds.selectedSpecies || undefined,
        speciesCode: speciesAndBreeds.selectedSpecies?.code,
        name: validation.patient.name,
        notes: validation.patient.notes
      }
    };
    if (speciesAndBreeds.selectedBreed) {
      contactDetails.patient.breed = speciesAndBreeds.selectedBreed;
      contactDetails.patient.breedCode = speciesAndBreeds.selectedBreed.code;
    }
    if (onContactDetailsChanged) {
      onContactDetailsChanged(contactDetails);
    }
  }, [
    provetContext.bookingFlowState.formData.contactDetails,
    validation.client.firstName,
    validation.client.lastName,
    validation.client.streetAddress,
    validation.client.zipCode,
    validation.client.city,
    validation.client.identificationNumber,
    validation.client.extraField,
    validation.patient.name,
    validation.patient.notes,
    speciesAndBreeds.selectedSpecies,
    speciesAndBreeds.selectedBreed,
    onContactDetailsChanged
  ]);

  /**
   * Validates the form and submits if valid, otherwise handles errors.
   */
  const validateAndSubmitForm = useCallback(async (): Promise<void> => {
    validation.setFormValidated(true);

    // Check if species is valid by checking both validation state and actual data
    // This ensures preselected species (not manually selected) are also considered valid
    const isSpeciesValid =
      validation.patientValidation.petSpeciesValid || !!speciesAndBreeds.selectedSpecies;

    // Note: phoneNumber and email validation removed - they're now validated in the phone verification step
    const isFormValid =
      validation.clientValidation.identificationNumberValid &&
      validation.clientValidation.firstNameValid &&
      validation.clientValidation.lastNameValid &&
      validation.clientValidation.cityValid &&
      validation.clientValidation.streetAddressValid &&
      validation.clientValidation.zipCodeValid &&
      validation.patientValidation.petNameValid &&
      isSpeciesValid &&
      validation.patientValidation.petNotesValid;

    if (!isFormValid) {
      // Use the validation error callback from parent if provided (matching Blazor behavior)
      if (onValidationError) {
        onValidationError();
      } else {
        // Fallback to local validation error handling
        await handleValidationError("#provet-contact-details", ".validation-error");
      }

      // Focus on the first invalid field for better UX
      if (
        !validation.clientValidation.identificationNumberValid &&
        validation.identificationNumberRef.current
      ) {
        validation.identificationNumberRef.current.focusAsync();
      } else if (
        !validation.patientValidation.petNotesValid &&
        validation.petNotesRef.current
      ) {
        validation.petNotesRef.current.focusAsync();
      }

      return;
    }

    contactDetailsChanged();
  }, [
    validation.clientValidation.identificationNumberValid,
    validation.clientValidation.firstNameValid,
    validation.clientValidation.lastNameValid,
    validation.patientValidation.petNameValid,
    validation.patientValidation.petSpeciesValid,
    validation.patientValidation.petNotesValid,
    speciesAndBreeds.selectedSpecies,
    onValidationError,
    handleValidationError,
    contactDetailsChanged,
    validation.client,
    validation.patient,
    speciesAndBreeds.selectedSpecies,
    speciesAndBreeds.selectedBreed
  ]);

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    validateAndSubmitForm();
  };

  return (
    <div id="provet-contact-details" className="flex flex-col gap-2xl">
      {/* Customer Information Section */}
      <div className="flex flex-col gap-md">
        <ProvetContactDetailsSectionHeader
          title={translationContext?.translations?.customerInformation || ""}
        />
        <CustomerInfoSection
          language={provetContext.language}
          validationHook={validation}
        />
      </div>

      {/* Patient Information Section */}
      <div className="flex flex-col gap-md">
        <ProvetContactDetailsSectionHeader
          title={translationContext?.translations?.patientInformation || ""}
        />
        <PatientInfoSection
          onSpeciesSelected={handleSpeciesSelected}
          validationHook={validation}
          speciesAndBreedsHook={speciesAndBreeds}
        />
        <RememberDetails
          checked={rememberDetailsChecked}
          onChange={setRememberDetailsChecked}
        />
      </div>

      {/* Submit Button */}
      <ProvetContactDetailsSubmit onSubmit={handleSubmit} />
    </div>
  );
};

export default ProvetContactDetails;
